/**
 * SectionHeading - consistent section intro ("Data Product" v3).
 * Green dash-kicker (uppercase, optional "01 /" number prefix) + a heavy
 * grotesk title (optional `accent` word in green) + muted subtitle, with an
 * optional right-aligned `actions` slot. Fades up on scroll by default.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeMode, displaySmSx } from '../../context/ThemeContext';
import { MotionInView, fadeUp } from '../../utils/animations';

function SectionHeading({
  eyebrow,
  number,
  title,
  accent,
  subtitle,
  align = 'left',
  actions,
  animate = true,
  as = 'h2',
  onDark = false,
  sx,
}) {
  const { palette } = useThemeMode();
  const center = align === 'center';
  const eyebrowText = number ? `${number} / ${eyebrow}` : eyebrow;
  const titleColor = onDark ? '#fff' : palette.text;
  const accentColor = onDark ? palette.accentLight : palette.accent;
  const subColor = onDark ? 'rgba(255,255,255,0.72)' : palette.textSecondary;

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: actions ? 'row' : 'column' },
        alignItems: actions ? { xs: 'flex-start', sm: 'flex-end' } : center ? 'center' : 'flex-start',
        justifyContent: actions ? 'space-between' : 'flex-start',
        gap: 2,
        textAlign: center ? 'center' : 'left',
        ...sx,
      }}
    >
      <Box sx={{ maxWidth: 720 }}>
        {eyebrowText && (
          <Typography
            component="div"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.1,
              justifyContent: center ? 'center' : 'flex-start',
              fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.13em',
              textTransform: 'uppercase', color: accentColor, mb: 1.5,
              '&::before': { content: '""', width: 22, height: '1.5px', bgcolor: accentColor, display: 'inline-block' },
            }}
          >
            {eyebrowText}
          </Typography>
        )}
        <Typography component={as} sx={{ ...displaySmSx, color: titleColor }}>
          {title}
          {accent && (
            <Box component="span" sx={{ color: accentColor }}>
              {title ? ' ' : ''}
              {accent}
            </Box>
          )}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              mt: 1.25,
              color: subColor,
              fontSize: { xs: '0.92rem', md: '0.98rem' },
              lineHeight: 1.6,
              maxWidth: 480,
              mx: center ? 'auto' : 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
    </Box>
  );

  if (!animate) return content;
  return <MotionInView variants={fadeUp}>{content}</MotionInView>;
}

export default SectionHeading;
